import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useBannerOffset } from "@hooks/useBannerOffset";

import { bannerSurface } from "@utils/bannerGradient";

/**
 * Site-wide green confirmation banner shown after the user recovers an
 * in-grace account via the AccountRestoreModal "Recover Account Now"
 * button. Persistent + dismissible — mirrors the visual + persistence
 * pattern of CancelledGlobalBanner (red) but in green for positive
 * confirmation.
 *
 * Trigger: AccountRestoreModal.handleRestore writes
 *   localStorage["account-just-recovered-at"] = ISO timestamp
 * immediately before its hard-nav to home. This banner reads that flag
 * on mount and on every route change, then renders until the user
 * dismisses via X.
 *
 * Dismiss key is scoped per-recovery-timestamp so a future recovery on
 * the same browser re-shows the banner (matches CancelledGlobalBanner's
 * per-subscription-id dismissal pattern).
 *
 * The recovery flag is cleared on logout (the standard logout.fulfilled
 * localStorage wipe — see feedback_logout_preserves_keys), so a
 * different user logging in afterwards doesn't see another user's
 * recovery confirmation.
 *
 * Auto-suppressed on /account-deleted: the grace-state landing page
 * shouldn't display a recovery confirmation (logically incompatible).
 */
const FLAG_KEY = "account-just-recovered-at";

const AccountRecoveredGlobalBanner: React.FC = () => {
  const { pathname } = useLocation();
  const [flagTimestamp, setFlagTimestamp] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let ts: string | null = null;
    try {
      ts = localStorage.getItem(FLAG_KEY);
    } catch { /* non-fatal */ }
    if (!ts) {
      setFlagTimestamp(null);
      setDismissed(false);
      return;
    }
    setFlagTimestamp(ts);
    try {
      setDismissed(localStorage.getItem(`account-recovered-banner-dismissed-${ts}`) === "1");
    } catch {
      setDismissed(false);
    }
  }, [pathname]);

  const showBanner = !!flagTimestamp && !dismissed;

  // Publish height to a CSS variable on EVERY page (it lives in the fixed
  // SecondHeader container) so the body padding-top reserves its strip and
  // content is pushed down below it instead of sliding behind.
  useBannerOffset("--account-recovered-banner-h", showBanner, bannerRef);

  if (!showBanner) return null;
  if (pathname === "/account-deleted") return null;

  const handleDismiss = () => {
    if (!flagTimestamp) return;
    try {
      localStorage.setItem(`account-recovered-banner-dismissed-${flagTimestamp}`, "1");
    } catch { /* non-fatal */ }
    setFadingOut(true);
    window.setTimeout(() => {
      setDismissed(true);
      setFadingOut(false);
    }, 360);
  };

  return (
    <div
      data-banner-surface
      ref={bannerRef}
      data-global-banner
      style={{
        width: "100%",
        ...bannerSurface("rgb(46, 125, 50)"),
        padding: fadingOut ? "0 24px" : "12px 24px",
        marginTop: -1,
        maxHeight: fadingOut ? 0 : 200,
        opacity: fadingOut ? 0 : 1,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        color: "#fff",
        fontFamily: '"Raleway", sans-serif',
        fontSize: "0.875rem",
        position: "relative",
        transition: "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <span style={{ fontWeight: 500 }}>
        Welcome back — your account has been fully restored.
      </span>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="banner-dismiss"
        style={{
          position: "absolute",
          top: "50%",
          right: 16,
          transform: "translateY(-50%)",
          width: 32,
          height: 32,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.98)",
          cursor: "pointer",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "color 0.15s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.98)"; }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default AccountRecoveredGlobalBanner;
